<script lang="ts">
    import DefaultContainer from "../DefaultContainer.svelte";
    import { BOARD_SIZE, state } from "../state";
    import Square from "./Square.svelte";
    import { indexToRowCol, rowColToIndex } from "../utils";
    import { otherOthelloSide } from "../message";
    import type { OthelloCell, OthelloSide } from "../message";
    import { fly, fade } from "svelte/transition";
    import { expoIn, circIn, cubicIn } from "svelte/easing";
    import Modal from "../Modal.svelte";
    import { onDestroy, onMount } from "svelte";

    let cells: Array<OthelloCell> = new Array(64).fill("unoccupied");
    $: blackCellCount = cells.filter((cell) => cell === "black").length;
    $: whiteCellCount = cells.filter((cell) => cell === "white").length;

    function handleClick(cellIndex: number) {
        if ($state.currentTurn !== $state.mySide) {
            return;
        }
        const { row, column } = indexToRowCol(cellIndex);
        const cell = cells[cellIndex];
        if (cell === "valid-move" && $state.currentTurn === $state.mySide) {
            $state.connection.applyMove(row, column);
            $state.currentTurn = otherOthelloSide($state.currentTurn);
        }
    }

    let gameOverModalTitle = "Tittel!";
    let gameOverModalReason = "Grunddd!";
    let gameOverModalVisible = false;

    $state.connection.registerMessageListener("game", (message) => {
        switch (message.type) {
            case "GameStateUpdated":
                $state.currentTurn = message.state.currentSide;
                cells = message.state.board;
                console.log("handling game state updated");
                break;
            case "YourTurn":
                $state.currentTurn = $state.mySide;
                if (message.validMoves.length > 0) {
                    $state.currentTurn = $state.mySide;

                    for (const validMove of message.validMoves) {
                        cells[rowColToIndex(validMove.row, validMove.column)] =
                            "valid-move";

                        // if ($state.autoTurn) {
                        //     const randomIndex = Math.floor(
                        //         Math.random() * message.validMoves.length
                        //     );
                        //     const rMove = message.validMoves[randomIndex];
                        //     setTimeout(() => {
                        //         $state.connection.applyMove(
                        //             rMove.row,
                        //             rMove.column
                        //         );
                        //     }, 50);
                        // }
                    }
                } else {
                    $state.currentTurn = otherOthelloSide($state.mySide);
                }
                break;
            case "GameOver":
                console.log("Handling GameOver");
                const status = message.status;
                console.log({ status });
                gameOverModalVisible = true;

                const blackWon = blackCellCount > whiteCellCount;
                const whiteWon = whiteCellCount > blackCellCount;
                const draw = blackCellCount === whiteCellCount;
                const youWon =
                    ($state.mySide === "black" && blackWon) ||
                    ($state.mySide === "white" && whiteWon);

                const reason = message.status.reason;
                gameOverModalReason =
                    reason === "BoardFull"
                        ? "The Board is Full."
                        : reason === "PlayerDisconnected"
                        ? "The Other Player disconnected."
                        : reason === "NoMoreMoves"
                        ? "No More Moves!"
                        : "bwahaha >:D";

                gameOverModalTitle = draw
                    ? "Draw!"
                    : youWon
                    ? "You Won!"
                    : "You Lost!";

                // You should always win if the other player quits.
                if (reason === "PlayerDisconnected") {
                    gameOverModalTitle = "You Won!";
                }

                break;
        }
    });
    onDestroy(() => $state.connection.unregisterMessageListener("game"));

    let bpointsWidth;
    let bpointsHeight;

    let wpointsWidth;
    let wpointsHeight;

    let mWidth;
    let mHeight;

    $: portraitOrientation = mWidth < mHeight - 80;

    $: amHeight = portraitOrientation
        ? mHeight - bpointsHeight - bpointsWidth - 20
        : mHeight - 60;

    $: amWidth = portraitOrientation
        ? mWidth - 110
        : mWidth - bpointsWidth - wpointsWidth - 110;
    $: mMin = Math.min(amWidth, amHeight);

    $: cellSize = mMin / BOARD_SIZE;

    let easing = cubicIn;
    $: flyParams = {
        y: -cellSize,
        duration: 300,
        easing,
    };

    let fadeParams = {
        duration: 300,
        easing,
    };

    function onGameOverModalClose() {
        gameOverModalVisible = false;
        setTimeout(() => ($state.currentPage = "menu"), 1000);
        setTimeout(() => window.location.reload(), 1400);
    }
</script>

<div class="container">
    <Modal
        titleText={gameOverModalTitle}
        reasonText={gameOverModalReason}
        visible={gameOverModalVisible}
        onClose={onGameOverModalClose}
    >
        <div slot="content" class="game-over-modal">
            <div class="points">
                <div class="piece-container">
                    <div class="piece white" />
                </div>
                <div class="count-indicator" />
                <h1>{whiteCellCount}</h1>
            </div>
            <div class="points">
                <div class="piece-container">
                    <div class="piece black" />
                </div>
                <div class="count-indicator" />
                <h1>{blackCellCount}</h1>
            </div>
        </div>
    </Modal>
    <div class="header">
        <h1>Othello</h1>
        <p>
            You are {$state.mySide}. It's {$state.currentTurn === $state.mySide
                ? ""
                : "not "}your turn.
        </p>
        <!-- <p>
            mWidth: {mWidth}. mHeight: {mHeight}. bpointsWidth: {bpointsWidth}.
            wpointsWidth: {wpointsWidth}. amWidth: {amWidth}. mMin: {mMin}.
            cellSize: {cellSize}.
        </p> -->
    </div>
    <main
        bind:clientWidth={mWidth}
        bind:clientHeight={mHeight}
        class:portrait={portraitOrientation}
    >
        <div
            class="points"
            bind:clientWidth={bpointsWidth}
            bind:clientHeight={bpointsHeight}
        >
            <div class="piece-container">
                <div class="piece black" />
            </div>
            <div class="count-indicator" />
            <h1>{blackCellCount}</h1>
        </div>
        <div class="cell-container">
            {#each cells as cell, i}
                <div
                    class="cell {cell}"
                    style="--size: {cellSize}"
                    on:touchend={() => handleClick(i)}
                    on:click={() => handleClick(i)}
                >
                    {#if cell === "black"}
                        <div
                            class="piece black"
                            in:fly={flyParams}
                            out:fade={fadeParams}
                        />
                    {:else if cell === "white"}
                        <div
                            class="piece white"
                            in:fly={flyParams}
                            out:fade={fadeParams}
                        />
                    {/if}
                </div>
            {/each}
        </div>
        <div
            class="points"
            bind:clientWidth={wpointsWidth}
            bind:clientHeight={wpointsHeight}
        >
            <div class="piece-container">
                <div class="piece white" />
            </div>
            <div class="count-indicator" />
            <h1>{whiteCellCount}</h1>
        </div>
    </main>
</div>

<style>
    .game-over-modal {
        display: flex;
        align-items: center;
        justify-content: space-around;
        gap: 0.4rem;
        width: 100%;
    }
    .game-over-modal .points {
        background: transparent;
        padding: 0;
        margin: 0;
        gap: 0rem;
        box-shadow: none;
    }

    .container {
        width: 100%;
        height: 100%;
        max-height: 100%;
        max-width: 100%;
        display: flex;
        flex-direction: column;
    }
    main {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        height: 0;
        flex-grow: 1;
        flex-shrink: 10;
    }
    main.portrait {
        flex-direction: column;
    }
    @media screen and (max-width: 300px) {
        .points {
            transform: scale(0.75);
        }
    }
    .points {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        align-items: center;
        justify-content: center;
        background-color: #f38ba0;
        width: max-content;
        padding: 1rem 0.8rem;
        margin: 1rem;
        border-radius: 100vmax;
        box-shadow: rgba(0, 0, 0, 0.15) 3px 3px 15px 4px;
    }
    main.portrait .points {
        flex-direction: row;
        padding: 0.5rem 1.5rem;
    }
    .points .piece-container {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 3rem;
        height: 3rem;
    }
    .points .piece {
        box-shadow: rgba(0, 0, 0, 0.2) 3px 3px 10px 1px;
        opacity: 0.9;
        --size: 45px;
    }
    .points > .count-indicator {
        --clength: 2rem;
        --cwidth: 0.3rem;
        width: var(--clength);
        height: var(--clength);
        display: flex;
        justify-content: center;
        align-items: center;
        opacity: 0.7;
    }
    .points > .count-indicator::before {
        content: "";
        display: block;
        width: var(--clength);
        height: var(--cwidth);
        border-radius: 100vmax;
        position: absolute;
        background-color: black;
        transform: rotate(45deg);
    }
    .points > .count-indicator::after {
        content: "";
        display: block;
        width: var(--clength);
        height: var(--cwidth);
        border-radius: 100vmax;
        position: absolute;
        background-color: black;
        transform: rotate(-45deg);
    }
    .points > h1 {
        margin-top: -0.6rem;
        font-size: 3.1rem;
        opacity: 0.8;
        color: rgb(15, 10, 5);
        text-align: center;
    }
    .header {
        grid-area: header;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
    }
    .cell-container {
        background-color: #ffbcbc;
        padding: 1.5rem;
        border-radius: 25px;
        display: grid;
        grid-template-columns: repeat(8, 1fr);
        box-shadow: rgba(0, 0, 0, 0.15) 3px 3px 15px 4px;
    }
    .cell {
        position: relative;
        /* --cell-size: 60; */
        max-width: calc(var(--size) * 1px);
        width: calc(var(--size) * 1px);
        max-height: calc(var(--size) * 1px);
        height: calc(var(--size) * 1px);
        border: 1px solid black;
        transition: background-color 0.6s ease;
        /* background-color: lime; */
    }
    .piece {
        position: absolute;
        --size: 80%;
        min-width: var(--size);
        min-height: var(--size);
        margin: calc((100% - var(--size)) / 2);
        border-radius: 50%;
        box-shadow: rgba(0, 0, 0, 0.1) 1px 1px 1px 1px;
    }
    .piece.black {
        background-color: rgb(15, 10, 5);
    }
    .piece.white {
        background-color: rgb(230, 255, 210);
    }
    /* .cell.white {
        background-color: white;
    }
    .cell.black {
        background-color: black;
    }
    */
    .cell.valid-move {
        background-color: yellow;
        cursor: pointer;
    }
</style>
